import { useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useCallback } from 'react';
import { toast } from 'sonner';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks need convex Id type for mutation params
import type { TAiReplyPlatform } from '@/@types/aiReply';

export function useAiReplySettings() {
  const settings = useQuery(api['aiReply/settings'].get);
  const upsertSettings = useMutation(api['aiReply/settings'].upsert);
  const overrides = useQuery(api['aiReply/contactOverrides'].list);
  const upsertOverride = useMutation(api['aiReply/contactOverrides'].upsert);
  const removeOverride = useMutation(api['aiReply/contactOverrides'].remove);
  const styleProfile = useQuery(api['aiReply/styleProfile'].get);
  const business = useQuery(api['businesses/queries'].getCurrentBusiness);
  const updateBusiness = useMutation(api['businesses/mutations'].updateBusiness);

  const updateSettings = useCallback(
    async (updates: Parameters<typeof upsertSettings>[0]) => {
      try {
        await upsertSettings(updates);
        toast.success('Settings saved');
      } catch {
        toast.error('Failed to save settings');
      }
    },
    [upsertSettings]
  );

  const toggleOverride = useCallback(
    async (
      contactIdentifier: string,
      platform: TAiReplyPlatform,
      field: 'disableAutoReply' | 'disableFollowUp' | 'disableStyleUsage',
      value: boolean,
      contactName?: string
    ) => {
      try {
        await upsertOverride({ contactIdentifier, platform, [field]: value, contactName });
        toast.success('Contact override updated');
      } catch {
        toast.error('Failed to update override');
      }
    },
    [upsertOverride]
  );

  const deleteOverride = useCallback(
    async (overrideId: Id<'aiReplyContactOverrides'>) => {
      try {
        await removeOverride({ overrideId });
        toast.success('Override removed');
      } catch {
        toast.error('Failed to remove override');
      }
    },
    [removeOverride]
  );

  const timezone = business?.timezone ?? 'Europe/Nicosia';

  const updateTimezone = useCallback(
    async (tz: string) => {
      try {
        await updateBusiness({ timezone: tz });
        toast.success('Timezone saved');
      } catch {
        toast.error('Failed to save timezone');
      }
    },
    [updateBusiness]
  );

  return {
    settings,
    overrides: overrides ?? [],
    styleProfile,
    timezone,
    updateSettings,
    updateTimezone,
    toggleOverride,
    deleteOverride,
    isLoading: settings === undefined,
  };
}
